import {
  isArray,
  isBoolean,
  isNumber,
  isObject,
  isPlainObject,
  isString,
} from 'lodash-es';
import { v4 as uuid } from 'uuid';
import { beautifyTableLabel } from '../beautifyTableLabel';
import { getJsonPathValue } from './json-path';

export type FieldType =
  | 'text'
  | 'number'
  | 'boolean'
  | 'date'
  | 'table'
  | 'object';

interface BaseFieldSchema<
  Type extends FieldType,
  Options extends Record<string, unknown> | undefined = undefined,
> {
  id: string;
  label: string;
  description?: string;
  type: Type;
  options?: Options;
}

export type TextFieldSchema = BaseFieldSchema<'text'>;
export type NumberFieldSchema = BaseFieldSchema<'number'>;
export type BooleanFieldSchema = BaseFieldSchema<'boolean'>;
export type DateFieldSchema = BaseFieldSchema<'date'>;
export type TableFieldSchema = BaseFieldSchema<
  'table',
  {
    columns: TableColumn[];
  }
>;
export type ObjectFieldSchema = BaseFieldSchema<
  'object',
  {
    properties: ObjectProperty[];
  }
>;

export type TableColumn = {
  id: string; // required only when header and accessorKey are not provided - could be used for computed values in the future
  header?: string;
  readonly accessorKey?: string;
  hidden?: boolean; // https://tanstack.com/table/v8/docs/examples/react/column-visibility
};

export type ObjectProperty = {
  header: string;
  readonly key: string;
  hidden?: boolean;
};

export type InputTypeToFieldType = {
  text: TextFieldSchema;
  number: NumberFieldSchema;
  boolean: BooleanFieldSchema;
  date: DateFieldSchema;
  table: TableFieldSchema;
  object: ObjectFieldSchema;
};

export type UISchema = InputTypeToFieldType[FieldType];

export const createUISchema = (
  json: Record<string, unknown>,
  jsonPath: string
): UISchema => {
  const value = getJsonPathValue(json, jsonPath);
  const id = uuid();
  const label = getPropertyKey(jsonPath);
  const inputField = {
    id,
    label: beautifyTableLabel(label),
    jsonPath,
  };

  // In the future, our first handlement should be with date
  if (isString(value)) {
    return {
      ...inputField,
      type: 'text',
    };
  }
  if (isNumber(value)) {
    return {
      ...inputField,
      type: 'number',
    };
  }
  if (isBoolean(value)) {
    return {
      ...inputField,
      type: 'boolean',
    };
  }
  if (isArray(value) && isSupportedForTableInput(value)) {
    return {
      ...inputField,
      type: 'table',
      options: {
        columns: createTableColumns(value as Record<string, unknown>[]),
      },
    };
  }
  if (isPlainObject(value)) {
    return {
      ...inputField,
      type: 'object',
      options: {
        properties: createObjectProperties(value as Record<string, unknown>),
      },
    };
  }

  throw new Error("Can't create UI schema for the given json path");
};

const getPropertyKey = (jsonPath: string) => jsonPath.split('.').pop() ?? '';

// An array will be considered valid for table input only if all of its items are objects
const isSupportedForTableInput = (array: unknown[]) => {
  return array.every(isObject);
};

const createTableColumns = (
  array: Record<string, unknown>[]
): TableColumn[] => {
  const columnsSet = new Set<string>();
  array.forEach((item) =>
    Object.keys(item).forEach((key) => columnsSet.add(key))
  );
  return Array.from(columnsSet).map((key) => ({
    id: uuid(),
    header: beautifyTableLabel(key),
    accessorKey: key,
  }));
};

const createObjectProperties = (obj: Record<string, unknown>) => {
  return Object.keys(obj).map((key) => ({
    header: beautifyTableLabel(key),
    key,
  }));
};
